const TRAILING_SLASH_RE = /\/$/
const LEADING_SLASH_RE = /^\//

export class WagtailClient {
  constructor(apiBase, preview, token, content_type, locale, host) {
    this.preview = preview
    this.token = token
    this.content_type = content_type || 'application/json'
    this.locale = locale

    this.baseURL = `${(apiBase || '').replace(TRAILING_SLASH_RE, '')}/v2`
    this.host = host
  }

  _getHeaders() {
    const headers = {
      'Accept-Language': this.locale,
    }
    if (!true && this.host) {
      headers.Host = new URL(this.host).host
    }
    return headers
  }

  _ensureTrailingSlash(path) {
    if (path.includes('preview')) {
      return path
    }

    return path.endsWith('/') ? path : `${path}/`
  }

  async _fetch(method, path, options = {}) {
    const normalizedPath = this._ensureTrailingSlash(path)
    const url = normalizedPath.startsWith('http')
      ? normalizedPath
      : `${this.baseURL}/${normalizedPath.replace(LEADING_SLASH_RE, '')}`

    const query = options.query || options.params
    const response = await $fetch(url, {
      method,
      headers: { ...this._getHeaders(), ...options.headers },
      query,
      body: options.body,
    })

    return { data: response }
  }

  async getGenericPages() {
    const res = await this._fetch('GET', 'pages/', {
      query: { type: 'cms.LumaBasePage', fields: 'url' },
    })

    const infos = res.data.items || []
    res.data.items = infos.filter(item => item.meta?.type === 'cms.LumaBasePage')

    return res
  }

  async getThankYouPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.ThankYouPage', fields: 'url' },
    })
  }

  async getTeamPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.TeamPage', fields: 'url' },
    })
  }

  async getEmployeePages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.EmployeePage', fields: 'url' },
    })
  }

  async getEventCourseOverviewPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.EventCourseOverviewPage', fields: 'url' },
    })
  }

  async getCoursePages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.CoursePage', fields: 'url' },
    })
  }

  async getLocationOverviewPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.LocationOverviewPage', fields: 'url' },
    })
  }

  async getLocationPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.LocationPage', fields: 'url' },
    })
  }

  async getJobPostingOverviewPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.JobPostingOverviewPage', fields: 'url' },
    })
  }

  async getJobPostingPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.JobPostingPage', fields: 'url' },
    })
  }

  async getCataloguePages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.CataloguePage', fields: 'url' },
    })
  }

  async getCategoryPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.CategoryPage', fields: 'url' },
    })
  }

  async getProductPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.ProductPage', fields: 'url' },
    })
  }

  async getMagazinePages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.MagazinePage', fields: 'url' },
    })
  }

  async getMagazineCategoryPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.MagazineCategoryPage', fields: 'url' },
    })
  }

  async getMagazineContentPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.MagazineContentPage', fields: 'url' },
    })
  }

  async getMagazineTagPages() {
    return this._fetch('GET', 'pages/', {
      query: { type: 'cms.MagazineTagPage', fields: 'url' },
    })
  }

  async getPageByPath(path) {
    const slice = path.slice(3)
    const nuxtApp = useNuxtApp()
    const { t } = useI18n()

    if (slice === 'eview/' && typeof window !== 'undefined') {
      const params = window.location.search
      return this._fetch('GET', `preview/${params}`)
    }
    const normalizedPath = this._ensureTrailingSlash(path)
    const fullUrl = `${this.baseURL}/pages/find/?html_path=${normalizedPath}`
    const fetchResponse = await globalThis.fetch(fullUrl, {
      method: 'GET',
      headers: this._getHeaders(),
      credentials: 'include',
      redirect: 'manual',
    })

    if (fetchResponse.status === 301 || fetchResponse.status === 302) {
      const location = fetchResponse.headers.get('location') || fetchResponse.headers.get('Location')
      if (location) {
        if (location.startsWith('/de/api/v2') || location.startsWith('/fr/api/v2')) {
          const language = location.split('/')[1]
          throw createError({
            statusCode: 404,
            message: t('error.404.title'),
            data: { buttonText: t('error.404.back'), title: t('error.404.pageTitle'), language },
          })
        }
        nuxtApp.runWithContext(() =>
          navigateTo(location, { redirectCode: fetchResponse.status, external: true }),
        )
        return {}
      }
    }

    if (!fetchResponse.ok) {
      const status = fetchResponse.status
      if (status === 404) {
        throw createError({
          statusCode: status,
          message: t('error.404.title'),
          data: { buttonText: t('error.404.back'), title: t('error.404.pageTitle') },
          fatal: true,
        })
      }
      throw createError({
        statusCode: status,
        message: t('error.500.title'),
        data: { buttonText: t('error.404.back') },
      })
    }

    const data = await fetchResponse.json()
    return { data }
  }

  async getPreviewPageByParams(params) {
    return this._fetch('GET', `preview/${params}`)
  }

  async getMenuItems() {
    return this._fetch('GET', 'menu/')
  }
}
